import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Container from '@/components/base/Container';
import { stats } from '@/mocks/site';
import { topics } from '@/mocks/topics';

export default function Hero() {
  const [keyword, setKeyword] = useState('');
  const navigate = useNavigate();

  const handleSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const query = keyword.trim();
    navigate(query ? `/tracking?q=${encodeURIComponent(query)}` : '/tracking');
  };

  const latest = [...topics].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt)).slice(0, 3);

  return (
    <section className="relative w-full overflow-hidden bg-foreground-950">
      <img
        src="https://readdy.ai/api/search-image?query=Cinematic%20abstract%20dark%20editorial%20backdrop%20with%20deep%20crimson%20and%20warm%20amber%20light%20streaks%2C%20geometric%20grid%20texture%2C%20moody%20atmospheric%20haze%2C%20high%20detail%2C%20no%20text%2C%20wide%20composition&width=1600&height=900&seq=kl-hero-main&orientation=landscape"
        alt="看料社娱乐热点追踪站首屏背景"
        title="看料社 - 娱乐热点追踪与最新进展"
        className="absolute inset-0 h-full w-full object-cover object-top"
      />
      <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/92 via-foreground-950/78 to-foreground-950/45"></div>

      <Container className="relative z-10 py-14 md:py-20">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1.35fr_1fr] lg:gap-14">
          <div className="kl-fade-up">
            <p className="mb-5 inline-flex items-center gap-2 rounded-full border border-accent-400/40 bg-accent-400/10 px-4 py-1.5 text-xs font-medium tracking-wide text-accent-300">
              <i className="ri-radar-line"></i>
              娱乐热点 · 一条线索追到底
            </p>
            <h1 className="font-heading text-3xl font-bold leading-tight text-background-50 md:text-5xl">
              看料黑料追踪，
              <br className="hidden md:block" />
              把每一次更新都记在同一条时间线上
            </h1>
            <p className="mt-5 max-w-xl text-sm leading-relaxed text-background-200 md:text-base">
              看料社专注娱乐热点与网络话题的持续跟进。从最初的线索、各方回应，到后续进展，我们按时间顺序整理成可追溯的专题，让你随时掌握最新动态。
            </p>

            <form onSubmit={handleSearch} className="mt-8 flex w-full max-w-xl flex-col gap-3 sm:flex-row">
              <div className="relative flex-1">
                <i className="ri-search-line pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-base text-foreground-400"></i>
                <input
                  type="search"
                  value={keyword}
                  onChange={(event) => setKeyword(event.target.value)}
                  placeholder="搜索你想跟进的热点线索"
                  aria-label="搜索追踪线索"
                  className="w-full rounded-md border border-background-50/20 bg-background-50/95 py-3.5 pl-11 pr-4 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-accent-400 focus:ring-2 focus:ring-accent-400/30"
                />
              </div>
              <button
                type="submit"
                className="inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-600 px-6 py-3.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-500"
              >
                <i className="ri-line-chart-line"></i>
                开始追踪
              </button>
            </form>

            <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-background-300">
              <span className="flex items-center gap-1.5">
                <i className="ri-check-double-line text-accent-400"></i>
                来源可追溯
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-check-double-line text-accent-400"></i>
                历史节点不删除
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-check-double-line text-accent-400"></i>
                不做主观推定
              </span>
            </div>
          </div>

          <aside className="hidden lg:block">
            <div className="rounded-lg border border-background-50/15 bg-background-50/10 p-6 backdrop-blur-sm">
              <div className="mb-4 flex items-center justify-between">
                <h2 className="flex items-center gap-2 font-heading text-base font-bold text-background-50">
                  <i className="ri-live-line text-primary-400"></i>
                  最新更新
                </h2>
                <Link to="/tracking" className="cursor-pointer text-xs text-accent-300 transition-colors hover:text-accent-200">
                  全部
                </Link>
              </div>
              <ul className="flex flex-col divide-y divide-background-50/15">
                {latest.map((topic) => (
                  <li key={topic.slug} className="py-3.5 first:pt-0 last:pb-0">
                    <Link to={`/topic/${topic.slug}`} className="group block cursor-pointer">
                      <div className="flex items-center gap-2 text-[11px] text-background-400">
                        <span className="text-accent-300">{topic.categoryName}</span>
                        <span className="h-1 w-1 rounded-full bg-background-500"></span>
                        <span>{topic.updatedAt}</span>
                      </div>
                      <p className="mt-1.5 text-sm font-medium leading-snug text-background-100 transition-colors group-hover:text-accent-300">
                        {topic.updates[topic.updates.length - 1].title}
                      </p>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        </div>

        <div className="mt-12 grid grid-cols-2 gap-6 border-t border-background-50/15 pt-8 lg:grid-cols-4">
          {stats.map((item) => (
            <div key={item.label}>
              <p className="font-heading text-2xl font-bold text-accent-400 md:text-3xl">{item.value}</p>
              <p className="mt-1 text-xs text-background-300 md:text-sm">{item.label}</p>
            </div>
          ))}
        </div>
      </Container>
    </section>
  );
}